import { Button, Divider, Stack, Typography } from '@mui/material';
import { AccessConditionEffect, type AccessCondition } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { type PermissionDescriptor } from '@/contexts/permissions';
import { useRoles } from '@/contexts/useRoles';

import { CustomRoleConditionEditorDialog } from './customRoleConditionEditorDialog';
import { RoleConditionComponent } from './roleConditionTooltip';
import { RolePermissions } from './rolePermissions';
import type { UpsertCustomRoleState } from './upsertCustomRoleFlow';

import { Accordion, AccordionDetails, AccordionSummary } from '../accordion';
import { ButtonWithIcon } from '../buttons/buttonWithIcon';
import { Icon } from '../shared/icon';
import { RenameEntityName } from '../wizard/renameEntityName';

export const CustomRoleScopesSelection = (
  props: StepProps<UpsertCustomRoleState>
) => {
  const { permissions } = useRoles();
  const [conditionDialogOptions, setConditionDialogOptions] = useState<{
    isOpen: boolean;
    permission?: PermissionDescriptor;
    roleCondition?: AccessCondition;
  }>({
    isOpen: false,
  });

  const setPermissionValue = (
    permission: PermissionDescriptor,
    value: boolean,
    accessConditionId?: string
  ) => {
    props.setWizardState((state) => ({
      ...state,
      permissionGrants: state.permissionGrants
        .filter((x) => x.permission !== permission.permissionType)
        .concat(
          value
            ? [
                {
                  permission: permission.permissionType,
                  accessConditionId,
                },
              ]
            : []
        ),
    }));
  };

  return (
    <StepContainer
      hideStepper
      onBackClick={props.back}
      backButtonText='Cancel'
      canGoNext={() => props.wizardState.permissionGrants.length > 0}
      nextButtonText={props.wizardState.id ? 'Save Changes' : 'Create Role'}
      onNextClick={() =>
        props.finish(
          props.wizardState.id
            ? `The control ${props.wizardState.name} changed successfully.`
            : `The control ${props.wizardState.name} created successfully.`
        )
      }
      navigationComponent={
        <RenameEntityName
          label='Role name'
          name={props.wizardState.name}
          onSubmit={(name) =>
            props.setWizardState((state) => ({ ...state, name }))
          }
        />
      }
    >
      <Stack className='grow'>
        <>
          <Accordion className='mt-[-24px] px-[52px]'>
            <AccordionSummary
              sx={{
                '.Mui-expanded': {
                  '.conditions-count': {
                    opacity: '0',
                  },
                },
              }}
            >
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                flexGrow={1}
              >
                <Typography variant='h4'>Data access control</Typography>
                <Stack
                  direction='row'
                  alignItems='center'
                  gap='4px'
                  className='conditions-count'
                  sx={{
                    transition: 'linear 0.1s',
                  }}
                >
                  <Icon iconClass='material-symbols-rule-rounded' />
                  <Typography sx={{ fontWeight: '400 !important' }}>
                    {(props.wizardState.accessConditions || []).length === 0
                      ? 'No access conditions'
                      : `${(props.wizardState.accessConditions || []).length} access condition${(props.wizardState.accessConditions || []).length === 1 ? '' : 's'}`}
                  </Typography>
                </Stack>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {(props.wizardState.accessConditions || []).length === 0 && (
                <Stack alignItems='center' gap='24px' className='py-[52px]'>
                  <Typography variant='h2'>
                    Limit access to your resources
                  </Typography>
                  <Typography
                    className='max-w-[580px]'
                    textAlign='center'
                    lineHeight='20px'
                  >
                    Set data access conditions to prevent a permission from
                    accessing some resources. Apply different conditions to each
                    permission to fine-tune your role.
                  </Typography>
                  <Button
                    variant='outlined'
                    onClick={() =>
                      setConditionDialogOptions({
                        isOpen: true,
                        roleCondition: undefined,
                        permission: undefined,
                      })
                    }
                  >
                    <i className='material-symbols-rule-rounded text-xl' />
                    <Typography className='font-semibold ml-[12px]'>
                      Create Access Conditions
                    </Typography>
                  </Button>
                </Stack>
              )}
              {(props.wizardState.accessConditions || []).length !== 0 && (
                <Stack>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    flexGrow={1}
                  >
                    <Typography>
                      {`This role has ${(props.wizardState.accessConditions || []).length} access condition${(props.wizardState.accessConditions || []).length === 1 ? '' : 's'}. ${(props.wizardState.accessConditions || []).length === 1 ? 'It' : 'They'} can be applied on permissions
                                    to control access.`}
                    </Typography>
                    <Button
                      onClick={() =>
                        setConditionDialogOptions({
                          isOpen: true,
                          roleCondition: undefined,
                          permission: undefined,
                        })
                      }
                    >
                      <i className='material-symbols-add-rounded text-xl' />
                      <Typography className='font-semibold ml-[12px]'>
                        Create Access Condition
                      </Typography>
                    </Button>
                  </Stack>
                  <Stack
                    direction='row'
                    gap='24px'
                    className='mt-[24px]'
                    flexWrap='wrap'
                  >
                    {(props.wizardState.accessConditions || []).map((rule) => (
                      <RoleConditionComponent
                        key={rule.id}
                        role={props.wizardState}
                        showActions={true}
                        roleCondition={rule}
                        onEdit={() =>
                          setConditionDialogOptions({
                            isOpen: true,
                            roleCondition: rule,
                            permission: undefined,
                          })
                        }
                        onDelete={() =>
                          props.setWizardState((state) => ({
                            ...state,
                            accessConditions: (
                              state.accessConditions || []
                            ).filter((x) => x.id !== rule.id),
                          }))
                        }
                      />
                    ))}
                  </Stack>
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
          <Divider sx={{ mx: '-40px', mt: '16px', mb: '40px' }} />
        </>
        <Stack
          className='px-[60px] grow'
          gap='24px'
          color={'var(--mui-palette-text-primary)'}
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
          >
            <Typography>
              Choose permissions to give users with this role:
            </Typography>
            <Stack direction='row' alignItems='center' gap='12px'>
              <Typography className='mr-[12px]'>Set all to:</Typography>
              <ButtonWithIcon
                className='border-none'
                variant='outlined'
                icon='material-symbols-check'
                text='Allow'
                onClick={() =>
                  props.setWizardState((state) => ({
                    ...state,
                    permissionGrants: Object.values(permissions).map(
                      (permission) => ({
                        permission: permission.permissionType,
                      })
                    ),
                  }))
                }
              />
              <ButtonWithIcon
                className='border-none'
                variant='outlined'
                icon='material-symbols-close-rounded'
                text='Block'
                onClick={() =>
                  props.setWizardState((state) => ({
                    ...state,
                    permissionGrants: [],
                  }))
                }
              />
            </Stack>
          </Stack>
          <RolePermissions
            role={props.wizardState}
            editMode
            onChange={(permission, value) =>
              setPermissionValue(permission, value)
            }
            onConditional={(permission, roleCondition) => {
              if (roleCondition) {
                setPermissionValue(permission, true, roleCondition.id);
              } else {
                setConditionDialogOptions({
                  isOpen: true,
                  permission: { ...permission },
                  roleCondition,
                });
              }
            }}
          />
        </Stack>
      </Stack>
      <CustomRoleConditionEditorDialog
        key={conditionDialogOptions.roleCondition?.id}
        open={conditionDialogOptions.isOpen}
        roleCondition={conditionDialogOptions.roleCondition}
        accessConditions={props.wizardState.accessConditions}
        onCancel={() =>
          setConditionDialogOptions({
            isOpen: false,
            roleCondition: undefined,
            permission: undefined,
          })
        }
        onSave={(name, condition, isInclusive) => {
          if (conditionDialogOptions.permission) {
            setPermissionValue(conditionDialogOptions.permission, true, name);
          }
          props.setWizardState((state) => ({
            ...state,
            accessConditions: (state.accessConditions || [])
              .filter((x) => x.id !== conditionDialogOptions.roleCondition?.id)
              .concat([
                {
                  id: name,
                  condition,
                  expression: null,
                  effect: isInclusive
                    ? AccessConditionEffect.RULE_EFFECT_INCLUSIVE
                    : AccessConditionEffect.RULE_EFFECT_EXCLUSIVE,
                },
              ]),
          }));
          setConditionDialogOptions({
            isOpen: false,
            roleCondition: undefined,
            permission: undefined,
          });
        }}
      />
    </StepContainer>
  );
};
